import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BuildVersionService } from '../../../services/build-version.service';
import { WeaveButtonComponent } from '../../../shared/forms/weave-button/weave-button.component';
import {
  GeneralWarningModalComponent,
} from '../modals/general-warning-modal/general-warning-modal.component';

/**
 * WAM-602 — the non-blocking "a new version is available" banner.
 *
 * Mounted once at the app root (`app.component.html`), it renders nothing
 * until `BuildVersionService.staleBundleDetected()` flips to `true` on a
 * CONFIRMED mismatch (never on a failed/absent check, and never on a single
 * unconfirmed poll — see that service's class doc). It is a slim, dismiss-free
 * bar rather than a modal: it never covers the page or stops the user from
 * continuing to work, which is deliberate — write requests are what actually
 * get refused (`staleBundleWriteGuardInterceptor`), not reading or editing.
 *
 * Reload is a real navigation (`location.reload()`), and that DISCARDS any
 * unsaved in-memory edits — it is not a safe, lossless action the way the
 * write guard's own refusal is. Confirming first (via the same
 * `GeneralWarningModalComponent` other destructive actions in this app use)
 * and naming that risk explicitly is the point: nothing here can tell
 * whether the user has unsaved changes on screen, so it must ask rather than
 * claim reloading is harmless.
 */
@Component({
  selector: 'app-stale-bundle-banner',
  templateUrl: './stale-bundle-banner.component.html',
  styleUrls: ['./stale-bundle-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [WeaveButtonComponent],
})
export class StaleBundleBannerComponent {
  private readonly dialog = inject(MatDialog);
  protected readonly buildVersion = inject(BuildVersionService);

  protected reloadWithConfirmation(): void {
    this.dialog
      .open(GeneralWarningModalComponent, {
        width: '600px',
        data: {
          title: 'Reload to update?',
          message:
            'Reloading discards anything unsaved on this page. Copy anything you still need before continuing.',
          question: 'Reload now?',
          confirmText: 'Reload',
          cancelText: 'Not yet',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed === true) this.reload();
      });
  }

  /** Not private so a spec can spy on it without ever exercising the real `window.location.reload()`/`location.href` navigation — see the comments below. */
  protected reload(): void {
    // Anti-lockout (WAM-602 review #2): lets the NEXT check tell a genuine
    // fix apart from the browser handing back the exact same stale bundle.
    // sessionStorage can throw (private/incognito mode, a locked-down
    // policy) — when it does, fall back to a `_wam602=<ownId>` query param
    // on the reload URL instead (WAM-602 review round 2 #6): a lockout that
    // silently can't even be DETECTED is worse than an ugly URL, and the
    // query param also busts any cache keyed on the URL.
    const markedViaSessionStorage = this.buildVersion.markReloadRequested();

    // Neither branch is mockable under this app's jsdom config (locked
    // descriptors) — same convention as every other `window.location.reload()`
    // call site (`annual-update.service.ts`, `accreditation.service.ts`,
    // `unified-dashboard.component.ts`): exercised manually, not in a spec.
    if (markedViaSessionStorage) {
      window.location.reload();
    } else {
      window.location.href = this.buildVersion.buildReloadUrlWithFallbackMarker();
    }
  }
}
